
import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import { LineChart, Line, ResponsiveContainer } from 'recharts'

const Dashboard = () => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [metrics, setMetrics] = useState(null)
  const [recentTrades, setRecentTrades] = useState([])
  const [chartData, setChartData] = useState([])
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch analytics overview
        const analyticsResponse = await api.analytics.getOverview({ period: '30d' })
        setMetrics(analyticsResponse.data.metrics)

        // Fetch recent trades
        const tradesResponse = await api.trades.getAll({ limit: 5 })
        setRecentTrades(tradesResponse.data.trades || [])

        // Generate chart data for P&L trend
        const allTradesResponse = await api.trades.getAll({ status: 'closed', limit: 30 })
        const allTrades = allTradesResponse.data.trades || []
        
        // Create daily P&L data for mini chart
        const dailyPnL = {}
        allTrades.forEach(trade => {
          if (trade.pnl && trade.exit_time) {
            const date = new Date(trade.exit_time).toISOString().split('T')[0]
            if (!dailyPnL[date]) {
              dailyPnL[date] = 0
            }
            dailyPnL[date] += trade.pnl
          }
        })

        const chartData = Object.entries(dailyPnL)
          .sort(([a], [b]) => new Date(a) - new Date(b))
          .slice(-7) // Last 7 days
          .map(([date, pnl]) => ({ date, pnl }))

        setChartData(chartData)

        setLoading(false)
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        // For demo purposes, set some default values if API fails
        setMetrics({
          totalPnL: 0,
          winRate: 0,
          totalTrades: 0,
          profitFactor: 0
        })
        setRecentTrades([])
        setLoading(false)
      }
    }

    if (user) {
      fetchDashboardData()
    }
  }, [user])

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0)
  }

  if (loading) {
    return (
      <div className="relative min-h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    )
  }

  return (
    <div className="relative min-h-full">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-cyan-500 rounded-full mix-blend-multiply filter blur-xl opacity-10 animate-pulse"></div>
      </div>
      
      <div className="relative p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-bold bg-gradient-to-r from-white via-cyan-200 to-purple-200 bg-clip-text text-transparent">
                Welcome back, {user?.firstName}! 🚀
              </h1>
              <p className="text-gray-300 mt-1">
                Here's your trading performance overview
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total P&L</p>
                  <p className="text-2xl font-bold text-white mt-1">{formatCurrency(metrics?.totalPnL || 0)}</p>
                  <div className={`flex items-center mt-2 text-sm ${(metrics?.totalPnL || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(metrics?.totalPnL || 0) >= 0 ? '+' : ''}{((metrics?.totalPnL || 0) / 10000 * 100).toFixed(1)}%</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Win Rate</p>
                  <p className="text-2xl font-bold text-white mt-1">{(metrics?.winRate || 0).toFixed(1)}%</p>
                  <div className={`flex items-center mt-2 text-sm ${(metrics?.winRate || 0) >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(metrics?.winRate || 0) >= 50 ? 'Above average' : 'Below average'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Total Trades</p>
                  <p className="text-2xl font-bold text-white mt-1">{metrics?.totalTrades || 0}</p>
                  <div className="flex items-center mt-2 text-sm text-blue-400">
                    <span>{recentTrades.length} recent</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-300">Profit Factor</p>
                  <p className="text-2xl font-bold text-white mt-1">{(metrics?.profitFactor || 0).toFixed(2)}</p>
                  <div className={`flex items-center mt-2 text-sm ${(metrics?.profitFactor || 0) >= 1 ? 'text-green-400' : 'text-red-400'}`}>
                    <span>{(metrics?.profitFactor || 0) >= 1 ? 'Profitable' : 'Needs work'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Charts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Profit Factor Chart */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-300 mb-4">Profit Factor</p>
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="6"
                      fill="none"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke={(metrics?.profitFactor || 0) >= 1 ? "#10b981" : "#ef4444"}
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - Math.min((metrics?.profitFactor || 0) / 3, 1))}`}
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-lg font-bold text-white">{(metrics?.profitFactor || 0).toFixed(2)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Win/Loss Donut Chart */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-300 mb-4">Win vs Loss</p>
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-20 h-20 transform -rotate-90">
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke="rgba(255,255,255,0.1)"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="40"
                      cy="40"
                      r="32"
                      stroke="#10b981"
                      strokeWidth="8"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 32}`}
                      strokeDashoffset={`${2 * Math.PI * 32 * (1 - (metrics?.winRate || 0) / 100)}`}
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-lg font-bold text-white">{(metrics?.winRate || 0).toFixed(0)}%</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Average Win vs Loss Bars */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-300 mb-4">Avg Win vs Loss</p>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Win</span>
                      <span>{formatCurrency(metrics?.avgWin || 0)}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className="bg-green-500 h-2 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${Math.min((metrics?.avgWin || 0) / Math.max(metrics?.avgWin || 1, Math.abs(metrics?.avgLoss || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Loss</span>
                      <span>{formatCurrency(Math.abs(metrics?.avgLoss || 0))}</span>
                    </div>
                    <div className="w-full bg-white/10 rounded-full h-2">
                      <div 
                        className="bg-red-500 h-2 rounded-full transition-all duration-1000 ease-out"
                        style={{ width: `${Math.min(Math.abs(metrics?.avgLoss || 0) / Math.max(metrics?.avgWin || 1, Math.abs(metrics?.avgLoss || 1)) * 100, 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* P&L Trend Mini Chart */}
            <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 shadow-2xl">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-300 mb-4">P&L Trend</p>
                <div className="text-lg font-bold text-white mb-2">{formatCurrency(metrics?.totalPnL || 0)}</div>
                {chartData.length > 0 ? (
                  <div className="h-12">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <Line 
                          type="monotone" 
                          dataKey="pnl" 
                          stroke={(metrics?.totalPnL || 0) >= 0 ? "#10b981" : "#ef4444"}
                          strokeWidth={2}
                          dot={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-12 flex items-center justify-center text-gray-400 text-xs">
                    No data
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl p-8 shadow-2xl">
            <h3 className="text-xl font-bold text-white mb-6">Recent Trades</h3>
            {recentTrades.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-300 mb-4">No trades yet</p>
                <p className="text-gray-400 text-sm">Start trading to see your activity here</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-black/20">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Symbol</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Side</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">P&L</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {recentTrades.map((trade, index) => (
                      <tr key={trade.id || index} className="hover:bg-white/5 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">{trade.symbol}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.side === 'buy' 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30'
                              : 'bg-red-500/20 text-red-400 border-red-500/30'
                          }`}>
                            {trade.side?.toUpperCase() || 'N/A'}
                          </span>
                        </td>
                        <td className={`px-6 py-4 whitespace-nowrap text-sm ${
                          (trade.pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {trade.pnl ? formatCurrency(trade.pnl) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full border ${
                            trade.status === 'closed'
                              ? 'bg-gray-500/20 text-gray-400 border-gray-500/30'
                              : trade.status === 'open'
                              ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                              : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          }`}>
                            {trade.status?.toUpperCase() || 'UNKNOWN'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard